
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useAd } from '../contexts/AdContext';
import tournamentService from '../services/tournamentService';
import { calculateAverageStack } from '../utils/viewHelpers';
import { useLocalTournamentTimer } from '../features/tournament/useLocalTournamentTimer';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, KeyboardSensor, useSensors, useSensor, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { PageContainer, ScrollableTableWrapper, TableContainer } from '../styles/controllerPageStyles';
import ActionButtons from '../components/controller/ActionButtons';
import TimerControls from '../components/controller/TimerControls';
import PlayerProgress from '../components/controller/PlayerProgress';
import StructureHeader from '../components/structure/StructureHeader';
import StructureBody from '../components/structure/StructureBody';
import StructureOverlay from '../components/structure/StructureOverlay';
import ControllerModals from '../components/controller/ControllerModals';
import ShareModal from '../components/controller/ShareModal';
import IntegratedDisplayView from '../components/controller/IntegratedDisplayView';
import { useDisplaySettings } from '../features/display/useDisplaySettings';
import { useControllerSettings } from '../features/tournament/useControllerSettings';
import { useControllerModals } from '../features/controller/useControllerModals';
import { useControllerData } from '../features/controller/useControllerData';
import { useTournamentIO } from '../features/controller/useTournamentIO';
import { useDisplayWakeLock } from '../features/display/useDisplayWakeLock';

const ControllerPage = () => {
    const { t } = useLocale();
    const navigate = useNavigate();
    const { tournamentId } = useParams();
    const { state: locationState } = useLocation();
    const { config: appConfig } = useAppConfig();
    const { showNotification } = useNotification();
    const { currentUser } = useAuth();
    const { updateAdVisibility } = useAd();
    const fileInputRef = useRef(null);
    const tournamentStateRef = useRef(null);

    // Custom Hook for Modal Logic
    const {
        modals,
        setModals,
        eventEditor,
        setEventEditor,
        editingInfoText,
        setEditingInfoText,
        handleEditEvent
    } = useControllerModals();

    // Prevent Host PC from sleeping while controlling timer
    useDisplayWakeLock();

    // Custom Hook for Data Fetching
    const { initialData, setInitialData, isLoading } = useControllerData(tournamentId, locationState, t, navigate, showNotification);

    const {
        tournamentState,
        isReadyEditing,
        setIsReadyEditing,
        actions: tournamentActions,
        timerState,
        remainingTime,
        rowIndex,
        tournamentName,
        tournamentDetails,
        columns,
        rows,
        events,
    } = useLocalTournamentTimer(initialData);

    const { handleFileSave, handleFileLoad } = useTournamentIO({
        tournamentName,
        tournamentDetails,
        columns,
        rows,
        events,
        setInitialData,
        setIsReadyEditing,
        tournamentId,
        showNotification,
        t
    });

    useEffect(() => {
        tournamentStateRef.current = tournamentState;
    }, [tournamentState]);

    const [stepSeconds, setStepSeconds] = useState(10);
    const [isPortrait, setIsPortrait] = useState(window.matchMedia("(orientation: portrait)").matches);
    const [viewMode, setViewMode] = useState('control');

    const [currentTime, setCurrentTime] = useState(new Date());
    const canEditColumns = timerState === 'ready';
    const canDeleteRow = useCallback((tableRowIndex) => timerState === 'ready' || (timerState === 'pause' && tableRowIndex > rowIndex), [timerState, rowIndex]);
    const canInsertRow = useCallback((tableRowIndex) => timerState === 'ready' || (timerState === 'pause' && tableRowIndex >= rowIndex), [timerState, rowIndex]);
    const canDragRow = useCallback((tableRowIndex) => timerState === 'ready' || (timerState === 'pause' && tableRowIndex > rowIndex), [timerState, rowIndex]);
    const canEditSpecificRowContent = useCallback((tableRowIndex) => timerState === 'ready' || (timerState === 'pause' && tableRowIndex > rowIndex), [timerState, rowIndex]);
    const [activeId, setActiveId] = useState(null);
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor), useSensor(KeyboardSensor));

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const activeType = String(active.id).startsWith('col-') ? 'column' : 'row';
            if (activeType === 'column') {
                tournamentActions.handleColumnDragEnd(active, over);
            } else {
                tournamentActions.handleRowDragEnd(active, over);
            }
        }
        setActiveId(null);
    };

    const activeItem = activeId ? (
        String(activeId).startsWith('col-')
            ? {
                type: 'column',
                header: columns.find(c => c.id === activeId),
                cells: rows.map(row => ({ rowId: row.id, value: row[columns.find(c => c.id === activeId)?.label], type: row.type }))
            }
            : rows.find(r => r.id === activeId)
    ) : null;

    useEffect(() => {
        const mql = window.matchMedia("(orientation: portrait)");
        const handleOrientationChange = (e) => setIsPortrait(e.matches);
        mql.addEventListener('change', handleOrientationChange);

        let timer;
        if (viewMode === 'display') {
            timer = setInterval(() => setCurrentTime(new Date()), 1000);
        }
        return () => {
            mql.removeEventListener('change', handleOrientationChange);
            clearInterval(timer);
        };
    }, [viewMode]);

    const { settings: controllerSettings, updateSetting: updateControllerSetting } = useControllerSettings();
    const { settings: displaySettings, actions: displayActions } = useDisplaySettings(
        tournamentState?.structure,
        rowIndex,
        remainingTime,
        timerState,
        null, // lastTimerUpdateTriggerType - no longer needed
        null, // lastP2PMessage - no longer needed
        controllerSettings.isIntegratedViewEnabled && viewMode === 'display', // Only enable if setting is on AND we are in display mode
        false // isPrimaryDisplay: Controller is secondary
    );

    const displayUrl = `${window.location.origin}${process.env.PUBLIC_URL}/t/${tournamentId}`;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(displayUrl).then(() => {
            showNotification(t('common.urlCopied'), 'success');
        }, (err) => {
            showNotification(t('common.urlCopyFailed'), 'error');
        });
    };

    useEffect(() => {
        const fetchOwnerTier = async () => {
            if (tournamentState && tournamentState.owner_id) {
                const { data, error } = await tournamentService.getUserTier(tournamentState.owner_id);
                if (error) {
                    updateAdVisibility(currentUser, 'free'); // fallback to free on error
                } else {
                    const tier = data?.tier;
                    updateAdVisibility(currentUser, tier);
                }
            } else {
                updateAdVisibility(currentUser, 'free');
            }
        };

        fetchOwnerTier();
    }, [currentUser, tournamentState, updateAdVisibility]);


    const handleUpdateStructure = useCallback(async () => {
        const success = await tournamentActions.saveStructureToDb();
        if (success) {
            setIsReadyEditing(false);
            showNotification(t('structure.updated'), 'success');
        } else {
            showNotification(t('errors.saveError'), 'error');
        }
    }, [tournamentActions, setIsReadyEditing, showNotification, t]);

    const handlePlayerProgressChange = useCallback((key, valueOrAction) => {
        let newValue;
        if (typeof valueOrAction === 'number') {
            newValue = Math.max(0, valueOrAction);
        }
        else {
            const currentValue = tournamentDetails[key] || 0;
            if (valueOrAction === 'inc') newValue = currentValue + 1;
            else if (valueOrAction === 'dec') newValue = Math.max(0, currentValue - 1);
        }
        if (newValue !== undefined) {
            tournamentActions.setTournamentDetails({ ...tournamentDetails, [key]: newValue });
        }
    }, [tournamentDetails, tournamentActions]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    const handleStartTimer = () => {
        if (controllerSettings.isIntegratedViewEnabled) {
            displayActions.getAudioContext()?.resume();
        }
        tournamentActions.startTimer();
    };

    const enhancedTournamentActions = {
        ...tournamentActions,
        startTimer: handleStartTimer
    };

    if (viewMode === 'display' && controllerSettings.isIntegratedViewEnabled) {
        return (
            <IntegratedDisplayView
                structureData={tournamentState?.structure}
                tournamentDetails={tournamentDetails}
                tournamentName={tournamentName}
                remainingTime={remainingTime}
                rowIndex={rowIndex}
                currentTime={currentTime}
                isPortrait={isPortrait}
                timerState={timerState}
                rows={rows}
                appConfig={appConfig}
                tournamentActions={enhancedTournamentActions}
                setViewMode={setViewMode}
                displaySettings={displaySettings}
                displayActions={displayActions}
            />
        );
    }

    return (
        <PageContainer>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveId(null)}>
                <TimerControls
                    rows={rows}
                    rowIndex={rowIndex}
                    remainingTime={remainingTime}
                    timerState={timerState}
                    isReadyEditing={isReadyEditing}
                    stepSeconds={stepSeconds}
                    appConfig={appConfig}
                    t={t}
                    tournamentActions={enhancedTournamentActions}
                    setModals={setModals}
                    setStepSeconds={setStepSeconds}
                />

                <PlayerProgress
                    tournamentDetails={tournamentDetails}
                    handlePlayerProgressChange={handlePlayerProgressChange}
                    calculateAverageStack={calculateAverageStack}
                    t={t}
                    isReadyEditing={isReadyEditing}
                />

                <ActionButtons
                    t={t}
                    timerState={timerState}
                    isReadyEditing={isReadyEditing}
                    rows={rows}
                    tournamentName={tournamentName}
                    setTournamentName={tournamentActions.setTournamentName}
                    touchStructure={() => setIsReadyEditing(true)}
                    handleUpdateStructure={handleUpdateStructure}
                    handleFileSave={handleFileSave}
                    handleFileLoad={handleFileLoad}
                    fileInputRef={fileInputRef}
                    structureActions={tournamentActions}
                    setModals={setModals}
                    controllerSettings={controllerSettings}
                    setViewMode={setViewMode}
                    displayUrl={displayUrl}
                    navigate={navigate}
                    setEditingInfoText={setEditingInfoText}
                    tournamentDetails={tournamentDetails}
                    canEditColumns={canEditColumns}
                />

                <ScrollableTableWrapper>
                    <TableContainer>
                        <SortableContext items={columns.map(c => c.id)} strategy={horizontalListSortingStrategy} disabled={!canEditColumns}><StructureHeader columns={columns} canEditColumns={canEditColumns} onDeleteColumn={tournamentActions.deleteColumn} onInsertColumn={tournamentActions.insertColumnAfter} onRenameColumn={tournamentActions.renameColumn} /></SortableContext>
                        <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
                            <StructureBody rows={rows} columns={columns} events={events} timerState={timerState} timerRowIndex={rowIndex} canEditSpecificRowContent={canEditSpecificRowContent} canDeleteRow={canDeleteRow} canInsertRow={canInsertRow} canDragRow={canDragRow} onChangeRow={tournamentActions.updateRowValue} onInsertRow={tournamentActions.insertRowAfter} onDeleteRow={tournamentActions.deleteRow} onInsertBreakRow={tournamentActions.insertBreakRow} onEditEvent={handleEditEvent} />
                        </SortableContext>
                    </TableContainer>
                </ScrollableTableWrapper>
                <DragOverlay>{activeId ? <StructureOverlay activeItem={activeItem} columns={columns} /> : null}</DragOverlay>
            </DndContext>

            <ControllerModals
                modals={modals}
                setModals={setModals}
                t={t}
                tournamentDetails={tournamentDetails}
                tournamentName={tournamentName}
                tournamentActions={tournamentActions}
                rows={rows}
                events={events}
                structureActions={tournamentActions}
                editingInfoText={editingInfoText}
                setEditingInfoText={setEditingInfoText}
                eventEditor={eventEditor}
                setEventEditor={setEventEditor}
                controllerSettings={controllerSettings}
                updateControllerSetting={updateControllerSetting}
                timerState={timerState}
                updateInfoText={tournamentActions.updateInfoText}
                handleScheduleTimer={tournamentActions.scheduleTimer}
            />

            <ShareModal
                isOpen={modals.share}
                onClose={() => setModals(m => ({ ...m, share: false }))}
                displayUrl={displayUrl}
                copyToClipboard={copyToClipboard}
            />
        </PageContainer>
    );
};
export default ControllerPage;